import React from 'react';
import { StepComponentProps } from './StepDefinition';
import { StepDefinition } from './StepDefinition';
import { AddinRoleAdditionMode, AddinRoleDTO, ProductForSaleDTO, Res, SoldProductType } from 'core/api';
import { ObjectUtils } from 'core/utils';
import { CatalogueFacade } from 'pos/facades';
import * as AddinComposer from './AddinComposer';
import * as SalesStore from '../../SalesStore';
import { StepLoader } from './StepLoader';
import { ScrollableGrid } from 'pos/ui';
import { getPrice } from './getPrice';
import { Message } from 'core/ui-next/modal';
import { Button, HStack, Spinner, VStack, Text, Box } from '@chakra-ui/react';
import { useAutoAdvance } from './useStepAutoAdvance';
import { ProductButton } from '../../sale/ProductButton';
import { CompositionState } from './Composition';
import { useCompositionSetters } from './useCompositionSetters';

type GetAddinsByRoleFn = (roleId: number, ticketId: number, isForMenu: boolean) => Promise<ProductForSaleDTO[]>;

function useAddinCatalogueCache() {
	return React.useMemo(() => {
		const rolesBySaleFormatId = new Map<number, Promise<AddinRoleDTO[]>>();
		const addinsByRole = new Map<string, Promise<ProductForSaleDTO[]>>();

		const getOrLoad = <TKey, TValue>(cache: Map<TKey, Promise<TValue>>, key: TKey, load: () => Promise<TValue>) => {
			const cachedValue = cache.get(key);
			if (cachedValue)
				return cachedValue;

			const value = load().catch(error => {
				cache.delete(key);
				throw error;
			});

			cache.set(key, value);
			return value;
		};

		return {
			getRoles: (saleFormatId: number) =>
				getOrLoad(rolesBySaleFormatId, saleFormatId, () => CatalogueFacade.getAddinRolesBySaleFormat(saleFormatId)),
			getAddins: (roleId: number, ticketId: number, isForMenu: boolean) => {
				const key = `${roleId}:${ticketId}:${isForMenu}`;
				return getOrLoad(addinsByRole, key, () => CatalogueFacade.getSaleableAsAddinByRole(roleId, ticketId, isForMenu));
			}
		};
	}, []);
}

async function validate(composition: CompositionState): Promise<boolean> {

	const rolesWithoutAllAddins = AddinComposer.getNotCompletedRolesByAllAddins(composition.addinsSelection.composition);

	if (rolesWithoutAllAddins.length) {
		const message = rolesWithoutAllAddins.map(role => Res.Web.COMPOSE_PRODUCT_ADDIN_GROUP_MUST_HAVE_ALL_ADDINS(role.Name, role.MaxAddins)).join('\r\n');
		await Message.show(message);
		return false;
	}

	const rolesWithoutMinAddins = AddinComposer.getNotCompletedRolesByMinAddins(composition.addinsSelection.composition);

	if (rolesWithoutMinAddins.length) {
		const message = rolesWithoutMinAddins.map(role => Res.Web.COMPOSE_PRODUCT_ADDIN_GROUP_MUST_HAVE_MIN(role.Name, role.MinAddins)).join('\r\n');
		await Message.show(message);
		return false;
	}

	return true;
}

function clean(composition: CompositionState): CompositionState {
	const newAddinsComposition = AddinComposer.clearAddins(composition.addinsSelection.composition);
	return { ...composition, addinsSelection: { ...composition.addinsSelection, composition: newAddinsComposition } };
}

export async function getAddinRolesForAddition(
	product: ProductForSaleDTO,
	productQuantity: number,
	getRoles: (saleFormatId: number) => Promise<AddinRoleDTO[]> = CatalogueFacade.getAddinRolesBySaleFormat
): Promise<{ mode: AddinRoleAdditionMode, roles: AddinRoleDTO[] }> {

	if (!product.HasAddins)
		throw new Error(`El producto ${product.Name} no está configurado para tener añadidos`);

	const roles = await getRoles(product.SaleFormatId);
	if (roles.length === 0)
		throw new Error(`El producto ${product.Name} no tiene ningún grupo de añadidos configurado`);

	const mode = roles[0].AdditionMode;
	if (roles.some(x => x.AdditionMode === AddinRoleAdditionMode.ChooseDifferentAddinPerUnit) && (roles.length > 1 || roles[0].MaxAddins > 1))
		throw new Error(`El producto ${product.Name} tiene más de un grupo de añadidos o un máximo de añadidos mayor que uno cuando está configurado para "elegir distinto añadido para cada unidad"`);

	// Ñapa: Para ChooseDifferentAddinPerUnit, cambio MaxAddins para que funcione la pantalla de seleccionar añadidos con el contador.
	if (mode === AddinRoleAdditionMode.ChooseDifferentAddinPerUnit)
		return { mode, roles: [{ ...roles[0], MaxAddins: productQuantity }] };

	return { mode, roles };
}

function AddinHeader({ composition }: { composition: AddinComposer.Composition }) {

	const currentAddinsCount = composition.selectedAddinsByRole[composition.currentRole.Id].length;
	const maxAddins = composition.currentRole.MaxAddins;
	const minAddins = composition.currentRole.MinAddins;
	const isRequired = minAddins > 0;
	const minimumRequiredAddinsColor = currentAddinsCount < minAddins ? 'warning' : 'info';

	return <HStack justifyContent={'space-between'} width={'full'}>
		<Box>
			<Text fontSize={'md'} fontWeight={'semibold'}>
				{composition.roles.length === 1 ?
					Res.Web.COMPOSE_SIMPLE_PRODUCT_ADDINS_HEADER_LBL()
					: Res.Web.COMPOSE_MULTIPLE_PRODUCT_ADDINS_HEADER_LBL()}
			</Text>
		</Box>
		<HStack gap={'4'} alignItems={'center'}>
			<Text fontSize={'sm'} fontWeight={'semibold'} color={isRequired ? minimumRequiredAddinsColor : { base: 'secondary', _dark: 'teal.700' }}>
				{Res.Web.COMPOSE_PRODUCT_SELECTED_ADDINS_LBL(currentAddinsCount, maxAddins) +
					(isRequired ?
						` ${Res.Web.COMPOSE_PRODUCT_MANDATORY_ADDINS_COUNT_LBL(minAddins)}`
						: ` ${Res.Web.COMPOSE_PRODUCT_SELECTED_OPTIONALS_ADDINS_LBL()}`)}
			</Text>
		</HStack>
	</HStack>;
}

function RolesItems(props: { roles: AddinRoleDTO[], currentRole: AddinRoleDTO, onRoleSelected: (role: AddinRoleDTO) => Promise<void> }) {
	return props.roles.length > 1 ?
		<Box bg={{ base: 'white', _dark: 'neutral.200' }} borderRadius={'md'}>
			{
				props.roles.map(x =>
					<Button
						key={x.Id}
						onClick={() => props.onRoleSelected(x)}
						bg={x.Id === props.currentRole.Id ? 'primary' : { base: 'white', _dark: 'neutral.200' }}
						color={x.Id === props.currentRole.Id ? 'white' : 'black'}
					>
						{x.Name}
					</Button>
				)
			}
		</Box>
		: null;
}

const AddinsComposition = (props: StepComponentProps & { getAddins: GetAddinsByRoleFn }) => {

	// TODO: Aquí tal cual hemos ido copiando la lógica que existía, pero a lo mejor después de maquetas se le puede dar una vuelta
	const { composition, setComposition, requestNext, resetIntroducedQty, getIntroducedQty, status, buttonSize, getAddins } = props;
	const addinsComposition = composition.addinsSelection.composition;
	const { setAddinsSelection } = useCompositionSetters(setComposition);
	const [isEditing, setIsEditing] = React.useState(false);

	const isCompleted = AddinComposer.isCompositionCompleted(addinsComposition);

	const { enableAutoAdvance } = useAutoAdvance({
		status,
		isCompleted,
		onAutoAdvance: requestNext,
	});

	const onAddinSelected = React.useCallback(async (addin: ProductForSaleDTO, introducedQuantity?: number) => {

		enableAutoAdvance();

		if (introducedQuantity) {
			const thisAddinCurrentQuantity = AddinComposer.getQuantity(addinsComposition, addinsComposition.currentRole.Id, addin);
			const maxQuantityToSet = AddinComposer.getPendingAddins(addinsComposition) + thisAddinCurrentQuantity;

			if (!addinsComposition.currentRole.AllowRepeat && introducedQuantity > 1) {
				await Message.show(Res.Msg.MSG_CANNOT_REPEAT_ADDIN_IN_ROLE(addinsComposition.currentRole.Name));
				return;
			}

			if (introducedQuantity > maxQuantityToSet) {
				await Message.show(Res.Msg.MSG_CANNOT_SET_ADDIN_QUANTITY(introducedQuantity, addinsComposition.currentRole.Name));
				return;
			}

			const newAddinsComposition = await AddinComposer.setAddinQuantity(addinsComposition, addin, introducedQuantity, getAddins);
			setAddinsSelection({ composition: newAddinsComposition });

		} else {
			const currentQuantity = AddinComposer.getQuantity(addinsComposition, addinsComposition.currentRole.Id, addin);

			if (!addinsComposition.currentRole.AllowRepeat && currentQuantity > 0) {
				const newAddinsComposition = AddinComposer.removeAddin(addinsComposition, addin);
				setAddinsSelection({ composition: newAddinsComposition });
				return;
			}

			// Si estoy editando los añadidos de una línea es posible que haya llegado al máximo y tenga la ventana
			// abierta porque es cómo ha empezado. En ese caso si se intenta añadir alguno aviso del error
			if (AddinComposer.isCurrentRoleCompleted(addinsComposition)) {
				await Message.show(Res.Msg.MSG_CANNOT_ADD_MORE_THAN_MAX_ADDINS_FOR_ROLE(addinsComposition.currentRole.MaxAddins, addinsComposition.currentRole.Name));
				return;
			}

			const newAddinsComposition = await AddinComposer.addAddin(addinsComposition, addin, getAddins, getPrice);
			setAddinsSelection({ composition: newAddinsComposition });
		}

		resetIntroducedQty();

	}, [addinsComposition, enableAutoAdvance, getAddins, resetIntroducedQty, setAddinsSelection]);

	const onRoleSelected = React.useCallback(async (role: AddinRoleDTO) => {
		const newAddinsComposition = await AddinComposer.selectRole(addinsComposition, role, getAddins);
		setAddinsSelection({ composition: newAddinsComposition });
	}, [addinsComposition, getAddins, setAddinsSelection]);

	//Controles para modo edición en el ProductButton
	const removeProduct = React.useCallback((addin: ProductForSaleDTO) => {
		const newAddinsComposition = AddinComposer.removeAddin(addinsComposition, addin);
		setAddinsSelection({ composition: AddinComposer.setAddinsEditionMode(newAddinsComposition, 'remove') });
	}, [addinsComposition, setAddinsSelection]);

	//Controles para modo edición en el ProductButton
	if (AddinComposer.isCompositionCompleted(addinsComposition) && status !== 'review')
		return <Spinner />;

	const currentAvailableAddins = addinsComposition.currentRoleAvailableAddins;
	const addinsButtons = currentAvailableAddins.map(addin =>
		<ProductButton
			key={addin.Id}
			value={addin}
			onClick={!isEditing ? async () => {

				const result = await getIntroducedQty();
				if (!result.ok)
					return;

				const introducedQuantity = result.value;
				await onAddinSelected(addin, introducedQuantity ?? undefined);
			} : undefined}
			size={buttonSize}
			disabled={!addin.IsAvailable}
			selected={AddinComposer.getQuantity(addinsComposition, addinsComposition.currentRole.Id, addin) > 0}
			quantity={AddinComposer.getQuantity(addinsComposition, addinsComposition.currentRole.Id, addin)}
			editionMode={isEditing}
			addProduct={() => onAddinSelected(addin)}
			removeProduct={() => removeProduct(addin)}
		/>);

	return <VStack width={'full'} height={'full'} gap={'6'} overflow={'auto'}>
		<RolesItems roles={addinsComposition.roles} onRoleSelected={onRoleSelected} currentRole={addinsComposition.currentRole} />
		<VStack width={'full'} height={'full'} gap={'4'} overflow={'auto'}>
			<AddinHeader composition={addinsComposition} />
			<HStack justifyContent={'flex-end'} width={'full'}>
				{addinsComposition.currentRole.AllowRepeat &&
					<Text onClick={() => setIsEditing(!isEditing)} fontSize={'xs'} color={{ base: 'primary', _dark: 'blue.400' }} cursor={'pointer'}>
						{!isEditing ? Res.Web.COMPOSE_PRODUCT_INIT_EDITION_QUANTITY_MODE_LBL() : Res.Web.COMPOSE_PRODUCT_EXIT_EDITION_MODE_LBL()}
					</Text>
				}
			</HStack>
			<ScrollableGrid
				items={addinsButtons}
				buttonSize={buttonSize}
				columnGap={'2'}
				rowGap={'4'}
			/>
		</VStack>
	</VStack>;
};

export const Component = (props: StepComponentProps) => {

	const addinCatalogueCache = useAddinCatalogueCache();

	const getData = React.useCallback(async (composition: CompositionState) => {

		const { selectedProduct, quantity, addinsSelection } = composition;

		if (selectedProduct === null)
			throw new Error('Product has not been selected');

		// Pido SIEMPRE añadidos si no estoy en estado nuevo, es decir estaba en estado completado o me están revisando. Si no dependo de la configuración del producto, porque 
		// si es la primera vez y no tiene solicitud automática de añadidos no los pido
		const askForAddins = props.status !== 'pending' || selectedProduct.HasAddins && selectedProduct.AskForAddins;
		const { mode, roles } = askForAddins ? await getAddinRolesForAddition(selectedProduct, quantity, addinCatalogueCache.getRoles) : { mode: AddinRoleAdditionMode.UseSameAddinForAll, roles: [] };

		if (roles.length === 0)
			throw new Error(`El producto ${selectedProduct.Name} no tiene ningún añadido. Revise la configuración del producto`);

		const previousAddins = AddinComposer.getAddins(addinsSelection.composition);

		let initialAddinComposition = await AddinComposer.buildComposition({
			mainProduct: selectedProduct,
			roles,
			previousAddins: previousAddins || [],
			addinMenuGroupName: props.settings.type === SoldProductType.MenuItem ? props.settings.groupName : '',
			ticketId: SalesStore.getTicket().Id
		}, addinCatalogueCache.getAddins);

		const defaultSelectedRole = addinsSelection.composition.currentRole.Id ? addinsSelection.composition.currentRole : AddinComposer.getNextRole(initialAddinComposition) ?? roles[0];
		initialAddinComposition = await AddinComposer.selectRole(initialAddinComposition, defaultSelectedRole, addinCatalogueCache.getAddins);
		initialAddinComposition = await AddinComposer.tryToComplete(initialAddinComposition, addinCatalogueCache.getAddins);
		initialAddinComposition = AddinComposer.setAddinsEditionMode(initialAddinComposition, addinsSelection.composition.mode);

		return { initialAddinComposition, addinRoleAdditionMode: mode };

	}, [addinCatalogueCache, props.settings.groupName, props.settings.type, props.status]);

	const getNextComposition = React.useCallback(
		({
			data,
			composition,
		}: {
			data: Awaited<ReturnType<typeof getData>>;
			composition: CompositionState;
		}): CompositionState => {

			const sameMode = composition.addinsSelection.mode === data.addinRoleAdditionMode;

			const { selectedAddinsByRole: ignoredCurrentSelectedAddinsByRole, ...currentComposition } = composition.addinsSelection.composition;
			const { selectedAddinsByRole: ignoredInitialSelectedAddinsByRole, ...initialComposition } = data.initialAddinComposition;

			// Es la misma composición si tiene los mismos añadidos, independientemente de donde estén. Esto lo miramos así
			// porque no sabemos a dónde va cada añadido cuando estamos en una edición, puede ser que la composición inicial
			// coloque los añadidos en roles en los que no los hemos colocado, pero que pueden estar en otros roles si aceptan
			// los mismos añadidos. Así que si son los mismos mantenemos el composition que estamos usando actualmente.
			const currentSelectedAddins = AddinComposer.getAddins(composition.addinsSelection.composition);
			const initialSelectedAddins = AddinComposer.getAddins(data.initialAddinComposition);
			const sameComposition = ObjectUtils.deepEquals(currentComposition, initialComposition) &&
				ObjectUtils.deepEquals([...currentSelectedAddins].sortBy(x => x.SaleFormatId), [...initialSelectedAddins].sortBy(x => x.SaleFormatId));

			if (sameMode && sameComposition) {
				return composition;
			}

			return {
				...composition,
				addinsSelection: {
					composition: data.initialAddinComposition,
					mode: data.addinRoleAdditionMode,
				}
			};
		},
		[]
	);

	return <StepLoader {...props} getData={getData} getNextComposition={getNextComposition}>
		{(data) => <AddinsComposition {...data} {...props} getAddins={addinCatalogueCache.getAddins} />}
	</StepLoader>;
};

const step: StepDefinition = {
	id: 'Addins',
	label: Res.Web.WIZARD_COMPOSE_SELECT_ADDINS_LBL(),
	canFinish: true,
	useKeyPad: true,
	validate,
	clean,
	Component
};

export default step;
